import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import LoginForm from '../components/LoginForm';
import { useToast } from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, success, removeToast } = useToast();

  const handleLoginSuccess = () => {
    success('Welcome back!', 'Successfully signed in.');
    
    // Check for redirect query parameter first (used by invite links)
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');
    
    // Then check location state (used by ProtectedRoute)
    const stateRedirect = (location.state as any)?.from?.pathname;
    
    const redirectTo = redirectParam || stateRedirect || '/dashboard';
    
    setTimeout(() => {
      navigate(redirectTo, { replace: true });
    }, 800);
  };

  return (
    <div className="min-h-screen w-full flex bg-gray-50">
      <ToastContainer toasts={toasts} onClose={removeToast} />
      
      {/* Left Side - Decorative Neo */}
      <div className="hidden lg:flex w-1/2 bg-[#161E54] relative overflow-hidden items-center justify-center p-12 border-r-4 border-black">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,#F16D34_2px,transparent_2px)] bg-[size:24px_24px]" />
        </div>

        <div className="relative z-10 max-w-lg text-[#F7F7F7] space-y-8" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <div className="w-20 h-20 bg-[#BBE0EF] border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center">
             <svg className="w-10 h-10 text-[#161E54]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="square" strokeLinejoin="miter" d="M13 10V3L4 14h7v7l9-11h-7z" />
             </svg>
          </div>
          
          <h1 className="text-6xl font-black tracking-tighter leading-tight text-[#BBE0EF]">
            ELEVATE<br/>
            YOUR TEAM<br/>
            RETROS.
          </h1>
          
          <p className="text-xl text-[#FF986A] font-medium max-w-md">
            Join thousands of agile teams using Retrospace to continuously improve, collaborate, and drive meaningful change.
          </p>

          <div className="flex -space-x-4 pt-4">
             {[1,2,3,4].map((i) => (
               <img 
                 key={i} 
                 src={`https://i.pravatar.cc/150?img=${i + 5}`} 
                 alt={`User ${i}`}
                 className="w-14 h-14 rounded-full border-4 border-black bg-[#BBE0EF] object-cover shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] relative transition-transform hover:-translate-y-2" 
                 style={{ zIndex: 10 - i }} 
               />
             ))}
             <div className="w-14 h-14 rounded-full border-4 border-black bg-[#F16D34] flex items-center justify-center text-xs font-black text-white z-0 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
               +2k
             </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-12 relative bg-[#F7F7F7]">
        <div className="w-full max-w-[480px] relative z-10">
          <div className="bg-white border-4 border-[#161E54] p-8 sm:p-12 shadow-neo lg:shadow-neo-lg transition-transform hover:-translate-y-1">
            <div className="mb-10 text-center">
              <h2 className="text-4xl font-black text-[#161E54] mb-4 uppercase tracking-tight">Welcome back</h2>
              <div className="h-2 w-20 bg-[#F16D34] mx-auto mb-6"></div>
              <p className="text-[#161E54] font-medium text-lg">
                Enter your details to sign in.
              </p>
            </div>

            <LoginForm onSuccess={handleLoginSuccess} />



            <div className="mt-6 text-center bg-[#BBE0EF] border-2 border-[#161E54] p-4 font-bold shadow-neo-sm">
              <p className="text-sm text-[#161E54]">
                Don't have an account?{' '}
                <Link 
                  to="/register" 
                  className="font-black text-[#F16D34] hover:text-[#161E54] hover:underline decoration-4 underline-offset-4"
                >
                  CREATE ACCOUNT
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
